import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Editor } from '@monaco-editor/react';
import { Button } from '../ui/button';
import Modal from '../ui/modal';
import TopBar from './TopBar';
import OpenTabsBar from './OpenTabsBar';
import TerminalPane from './TerminalPane';
import EditorStatusBar from './EditorStatusBar';
import ArcCanvasWorkspace from './ArcCanvasWorkspace';
import SyntaraCanvas from '../SyntaraCanvas';
import { useEditorStore } from '../../stores/useEditorStore';
import { useChatStore } from '../../stores/useChatStore';
import BlueprintIcon from '../icons/BlueprintIcon';
import DesignIcon from '../icons/DesignIcon';
import toast from 'react-hot-toast';
import { ErrorBoundary } from '../ErrorBoundary';
import { useSharedWS } from '../../hooks/useSocket';

// Dynamic error reporting import to improve chunking and avoid mixed imports
const reportErrorAsync = async (payload: {
  source: string;
  error: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any;
}) => {
  try {
    const mod = await import('../../utils/errorReporting');
    mod.reportError(payload);
  } catch {
    // noop
  }
};

import { 
  Globe, Diff, Camera, Search, RefreshCw, ExternalLink, Zap
} from 'lucide-react';
import { useEditorSocket } from '../../hooks/useEditorSocket';

import '../../styles/monaco-editor-fix.css';
import '../../styles/micro-interactions.css';
import type { CursorPosition, DocumentPatch } from '../../hooks/useEditorSocket';

interface PerformanceMetrics {
  renderTime: number;
  memoryUsage: number;
}

interface TestResults {
  passed: number;
  failed: number;
  total: number;
}

interface EnhancedMonacoEditorProps {
  socket?: {
    sendMessage: (message: unknown) => void;
    sendCursor: (x: number, y: number) => void;
    sendPatch: (delta: unknown) => void;
    reconnect: () => void;
    disconnect: () => void;
    ws: WebSocket | null;
    isConnected: boolean;
    isReconnecting: boolean;
    isOffline: boolean;
    connectedUsers: Array<{ id: string; name: string; color: string }>;
    cursors: Array<{ x: number; y: number; userId: string; user: { id: string; name: string; color: string } }>;
    reconnectAttempts: number;
    status: 'idle' | 'connecting' | 'open' | 'error';
  };
}

const EnhancedMonacoEditor: React.FC<EnhancedMonacoEditorProps> = ({ socket }) => {
  console.log('[EnhancedMonacoEditor] COMPONENT ENTRY - Component is being called');
  
  const isTestEnvironment = typeof window !== 'undefined' && (
    navigator.userAgent.includes('Playwright') ||
    navigator.userAgent.includes('HeadlessChrome') ||
    import.meta.env.MODE === 'test'
  );
  console.log('[EnhancedMonacoEditor] ENV CHECK', {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'undefined',
    mode: import.meta.env.MODE,
    isTestEnvironment
  });

  console.debug('[EnhancedMonacoEditor] Environment check:', {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'undefined',
    mode: import.meta.env.MODE,
    isTestEnvironment,
    socketProp: !!socket
  });

  console.debug('[EnhancedMonacoEditor] Socket initialization:', {
    isTestEnvironment,
    internalSocketWillBeCreated: !isTestEnvironment,
    socketProp: !!socket
  });

  const { activeTab, setActiveTab } = useEditorStore();
  const { 
    taskMetadata, 
    sessionId, 
    getEditorContent, 
    setEditorContent, 
    getActiveFile, 
    setActiveFile 
  } = useChatStore();
  const activeFile = getActiveFile();
  const code = getEditorContent(activeFile);
  const [showRefactorModal, setShowRefactorModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showTaskInference, setShowTaskInference] = useState(false);
  const [showCrossBranchSearch, setShowCrossBranchSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>(['[INFO] Monaco editor initialized', '[INFO] WebSocket connection established']);

  const [testResults] = useState<TestResults>({ passed: 12, failed: 2, total: 14 });
  const [performanceMetrics] = useState<PerformanceMetrics>({ renderTime: 45, memoryUsage: 23.4 });
  const [previewUrl, setPreviewUrl] = useState('http://localhost:3000');
  const [browserUrl, setBrowserUrl] = useState('https://chat.openai.com');
  const [scanningMode, setScanningMode] = useState<'off' | 'manual' | 'automatic'>('off');
  const [isScanning, setIsScanning] = useState(false);
  const [scannedContent, setScannedContent] = useState<any>(null);
  const [lastScanTime, setLastScanTime] = useState<Date | null>(null);
  const [layoutMode, setLayoutMode] = useState<'classic' | 'hive-grid'>('classic');
  const [showFallback, setShowFallback] = useState(false);

  const [isTerminalMinimized, setIsTerminalMinimized] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('beehive-terminal-minimized');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });

  const editorRef = useRef<any>(null);
  const tabs = ['editor', 'diff', 'browser', 'screenshots', 'canvas', 'syntara'] as const;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (scanningMode === 'automatic' && browserUrl && activeTab === 'browser') {
      const interval = setInterval(() => {
        performOpticalScan(browserUrl);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [scanningMode, browserUrl, activeTab]);

  const [remoteCursors, setRemoteCursors] = useState<Array<{x: number, y: number, userId: string, user: {id: string, name: string, color: string}}>>([]);
  const editorDomRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const socketCallbacks = {
    onMessage: (message: unknown) => {
      if (message && typeof message === 'object' && 'type' in message) {
        setLogs((prev: string[]) => [...prev, `[Editor] ${(message as { type: string }).type} received`]);
      }
    },
    onCursorUpdate: (cursor: CursorPosition) => {
      setRemoteCursors(prev => [
        ...prev.filter(c => c.userId !== cursor.userId),
        cursor
      ]);
    },
    onDocumentPatch: (patch: DocumentPatch) => {
      console.debug('[EnhancedMonacoEditor] Received document patch:', patch);
      if (editorRef.current) {
        try {
          const model = editorRef.current.getModel();
          if (model) {
            if ('type' in patch && patch.type === 'full-content' && 'content' in patch && typeof patch.content === 'string') {
              console.debug('[EnhancedMonacoEditor] Applying full content update:', patch.content);
              model.setValue(patch.content);
            }
            else if ('delta' in patch && typeof patch.delta === 'string') {
              console.debug('[EnhancedMonacoEditor] Applying patch to Monaco editor:', patch.delta);
              model.setValue(patch.delta);
            }
          }
        } catch (error) {
          console.error('Error applying document patch:', error);
          reportErrorAsync({
            source: 'EnhancedMonacoEditor.onDocumentPatch',
            error: error instanceof Error ? error.message : String(error),
            details: { action: 'document_patch_apply', sessionId }
          });
        }
      }
    },
    onConnectionChange: (isConnected: boolean) => {
      if (isConnected) {
        setLogs((prev: string[]) => [...prev, '[Editor] Connected to real-time collaboration']);
      } else {
        setLogs((prev: string[]) => [...prev, '[Editor] Disconnected from collaboration']);
      }
      
      if (editorRef.current) {
        editorRef.current.updateOptions({ readOnly: !isConnected });
      }
    },
    userId: `editor-user-${Math.random().toString(36).substr(2, 9)}`,
    userName: 'Editor User',
    userColor: '#3B82F6',
    sessionId: sessionId || undefined
  };

  const internalSocket = useEditorSocket(socketCallbacks);

  const editorSocket = socket || (isTestEnvironment ? {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    sendMessage: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    sendCursor: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    sendPatch: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    reconnect: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    disconnect: () => {},
    ws: null,
    isConnected: true,
    isReconnecting: false,
    isOffline: false,
    connectedUsers: [],
    cursors: [],
    reconnectAttempts: 0,
    status: 'open' as const
  } : internalSocket);

  const finalSocket = editorSocket;

  console.debug('[EnhancedMonacoEditor] Final socket state:', {
    editorSocket: !!editorSocket,
    finalSocket: !!finalSocket,
    finalSocketStatus: finalSocket?.status,
    isTestEnvironment
  });

  console.log('[EnhancedMonacoEditor] CRITICAL DEBUG - About to check early returns:', {
    finalSocketStatus: finalSocket?.status,
    isTestEnvironment,
    willReturnConnecting: finalSocket?.status === 'connecting',
    willReturnError: finalSocket?.status === 'error'
  });

  useEffect(() => {
    const editorDom = editorDomRef.current;
    if (!editorDom || !finalSocket?.isConnected) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = editorDom.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      if (x >= 0 && y >= 0 && x <= rect.width && y <= rect.height) {
        finalSocket?.sendCursor(x, y);
      }
    };

    editorDom.addEventListener('mousemove', handleMouseMove);
    return () => editorDom.removeEventListener('mousemove', handleMouseMove);
  }, [finalSocket, finalSocket?.isConnected]);

  const handlePresenceMessage = useCallback((event: MessageEvent) => {
    if (activeTab !== 'browser') return;
    
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'preview_update') {
        setIsLoading(false);
        setLogs((prev: string[]) => [...prev, '[WS] Preview updated']);
      }
    } catch (error) {
      console.log('Error parsing presence message:', error);
    }
  }, [activeTab]);

  useSharedWS('/presence', handlePresenceMessage);

  useEffect(() => {
    const handleTerminalToggle = () => {
      const newMinimized = !isTerminalMinimized;
      setIsTerminalMinimized(newMinimized);
      if (typeof window !== 'undefined') {
        localStorage.setItem('beehive-terminal-minimized', JSON.stringify(newMinimized));
      }
    };

    const handleBeeOutput = (_event: Event) => {
      if (isTerminalMinimized) {
        setIsTerminalMinimized(false);
        if (typeof window !== 'undefined') {
          localStorage.setItem('beehive-terminal-minimized', JSON.stringify(false));
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('beehive:terminal-toggle', handleTerminalToggle);
      window.addEventListener('beehive:bee-output', handleBeeOutput);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beehive:terminal-toggle', handleTerminalToggle);
        window.removeEventListener('beehive:bee-output', handleBeeOutput);
      }
    };
  }, [isTerminalMinimized]);


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    monaco.editor.defineTheme('beehive-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
        { token: 'keyword', foreground: '569CD6' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'type', foreground: '4EC9B0' },
        { token: 'function', foreground: 'DCDCAA' },
      ],
      colors: {
        'editor.background': '#0f172a',
        'editor.foreground': '#e2e8f0',
        'editorLineNumber.foreground': '#64748b',
        'editorLineNumber.activeForeground': '#94a3b8',
        'editor.selectionBackground': '#334155',
        'editor.inactiveSelectionBackground': '#1e293b',
        'editorCursor.foreground': '#f8fafc',
      }
    });

    monaco.editor.setTheme('beehive-dark');
    
    const decorations = [
      {
        range: new monaco.Range(5, 1, 5, 1),
        options: {
          isWholeLine: false,
          glyphMarginClassName: 'test-pass-glyph',
          glyphMarginHoverMessage: { value: 'Test passed: analyze_market()' }
        }
      },
      {
        range: new monaco.Range(8, 1, 8, 1),
        options: {
          isWholeLine: false,
          glyphMarginClassName: 'test-fail-glyph',
          glyphMarginHoverMessage: { value: 'Test failed: missing error handling' }
        }
      }
    ];
    
    editor.deltaDecorations([], decorations);
  };

  const handleFixWithARCOS = () => {
    setShowRefactorModal(true);
  };

  const toggleLayoutMode = () => {
    setLayoutMode(prev => prev === 'classic' ? 'hive-grid' : 'classic');
  };

  const handleUndo = async () => {
    if (!taskMetadata?.taskId || !sessionId) {
      toast.error('No active task found');
      return;
    }
    
    toast('Use the History panel to revert specific patches', {
      icon: '📋',
      duration: 3000
    });
  };

  const handleRedo = async () => {
    toast('Redo functionality coming soon', {
      icon: '🔄',
      duration: 2000
    });
  };



  const handleTerminalToggle = () => {
    const newMinimized = !isTerminalMinimized;
    setIsTerminalMinimized(newMinimized);
    if (typeof window !== 'undefined') {
      localStorage.setItem('beehive-terminal-minimized', JSON.stringify(newMinimized));
    }
  };

  const handleRefactor = () => {
    setShowRefactorModal(false);
    setLogs((prev: string[]) => [...prev, '[ARCOS] Applying AI refactor...', '[ARCOS] Refactor completed']);
  };

  const refreshPreview = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
    setLogs((prev: string[]) => [...prev, '[PREVIEW] Refreshing browser preview...']);
  };

  const scrapeWithBrowserAPI = async (url: string): Promise<any> => {
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      return {
        title: doc.title || '',
        text: doc.body?.innerText || '',
        description: doc.querySelector('meta[name="description"]')?.getAttribute('content') || '',
        images: Array.from(doc.images).map(img => img.src).slice(0, 5)
      };
    } catch (error) {
      try {
        const iframe = document.querySelector('iframe[title="Universal Browser Scanner"]') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const doc = iframe.contentDocument;
          return {
            title: doc.title || '',
            text: doc.body?.innerText || '',
            description: doc.querySelector('meta[name="description"]')?.getAttribute('content') || '',
            images: Array.from(doc.images).map(img => img.src).slice(0, 5)
          };
        }
      } catch (iframeError) {
        console.warn('Cannot access iframe content:', iframeError);
      }
      
      return {
        title: `Content from ${new URL(url).hostname}`,
        text: 'Content scanning blocked by CORS or X-Frame-Options headers. Manual scanning may be limited.',
        description: 'Site blocks automated content extraction',
        images: []
      };
    }
  };

  const performOpticalScan = async (url: string) => {
    if (isScanning) return;
    
    setIsScanning(true);
    try {
      const content = await scrapeWithBrowserAPI(url);
      
      if (content) {
        setScannedContent(content);
        setLastScanTime(new Date());
        
        setLogs(prev => [...prev, `[Scanner] Content captured from ${url}`]);
        
        toast.success(`Content scanned and processed from ${new URL(url).hostname}`);
      }
    } catch (error) {
      console.error('Optical scanning failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLogs(prev => [...prev, `[Scanner] Error: ${errorMessage}`]);
      toast.error('Scanning failed - check console for details');
    } finally {
      setIsScanning(false);
    }
  };

  const handleIframeError = () => {
    setShowFallback(true);
    setLogs(prev => [...prev, `[Browser] Iframe blocked by X-Frame-Options, using fallback mode`]);
  };

  const handleIframeLoad = () => {
    setShowFallback(false);
  };
  const executeCommand = async (command: string) => {
    switch (command) {
      case 'run':
        setLogs((prev: string[]) => [...prev, '[INFO] Running code...', '[INFO] Execution completed']);
        break;
      case 'save':
        setLogs((prev: string[]) => [...prev, '[INFO] File saved successfully']);
        break;
      case 'debug':
        setLogs((prev: string[]) => [...prev, '[DEBUG] Starting debug session...']);
        break;
      case 'generate-tasks':
        setShowTaskInference(true);
        setLogs((prev: string[]) => [...prev, '[AI] Generating task suggestions...', '[AI] Found 3 optimization opportunities']);
        break;
      case 'cross-branch-search':
        setShowCrossBranchSearch(true);
        setLogs((prev: string[]) => [...prev, '[SEARCH] Searching across branches...', '[SEARCH] Found 5 matches in feature/optimization']);
        break;
      case 'take-screenshot':
        setLogs(prev => [...prev, '[PLAYWRIGHT] Taking screenshots...', '[PLAYWRIGHT] Screenshots saved']);
        break;
      case 'promote-deployment': {
        setLogs(prev => [...prev, '[DEPLOY] Promoting to production...']);
        const promoteResult = { success: true, deploymentId: 'deploy-' + Date.now(), error: '' };
        if (promoteResult.success) {
          setLogs(prev => [...prev, '[DEPLOY] Promotion successful', `[DEPLOY] Deployment ID: ${promoteResult.deploymentId}`]);
        } else {
          setLogs(prev => [...prev, '[DEPLOY] Promotion failed', `[ERROR] ${promoteResult.error}`]);
        }
        break;
      }
      case 'rollback-deployment': {
        setLogs(prev => [...prev, '[DEPLOY] Rolling back deployment...']);
        const rollbackResult = { success: true, error: '' };
        if (rollbackResult.success) {
          setLogs(prev => [...prev, '[DEPLOY] Rollback completed', '[DEPLOY] Previous stable version restored']);
        } else {
          setLogs(prev => [...prev, '[DEPLOY] Rollback failed', `[ERROR] ${rollbackResult.error}`]);
        }
        break;
      }
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'editor':
        return (
          <div className="h-full flex flex-col relative" ref={editorDomRef}>
            <div className="flex-1">
              <Editor
                height="100%"
                language={activeFile.endsWith('.py') ? 'python' : 'json'}
                theme="beehive-dark"
                value={code}
                onChange={(value: string | undefined) => {
                  console.debug('[Monaco onChange] Value changed:', value);
                  console.debug('[Monaco onChange] finalSocket state:', {
                    exists: !!finalSocket,
                    isConnected: finalSocket?.isConnected,
                    sendPatch: typeof finalSocket?.sendPatch
                  });
                  
                  setEditorContent(value || '', activeFile);
                  
                  if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                  }
                  
                  debounceTimeoutRef.current = setTimeout(() => {
                    console.debug('[Monaco onChange debounced] About to check conditions:', {
                      finalSocketExists: !!finalSocket,
                      isConnected: finalSocket?.isConnected,
                      valueUndefined: value === undefined,
                      willSendPatch: !!(finalSocket?.isConnected && value !== undefined)
                    });
                    
                    if (finalSocket?.isConnected && value !== undefined) {
                      console.debug('[Monaco onChange] Calling sendPatch with value:', value);
                      finalSocket?.sendPatch(value);
                    } else {
                      console.debug('[Monaco onChange] NOT calling sendPatch - conditions not met');
                    }
                  }, 200);
                }}
                onMount={(editor, monaco) => handleEditorDidMount(editor, monaco)}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  selectOnLineNumbers: true,
                  automaticLayout: true,
                  glyphMargin: true,
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                  fontSize: 15,
                  fontFamily: '"JetBrains Mono", "Fira Code", "Inter", monospace',
                  lineHeight: 22,
                  letterSpacing: 0.5,
                  renderWhitespace: 'selection',
                  smoothScrolling: true,
                  cursorBlinking: 'expand',
                  cursorSmoothCaretAnimation: 'on'
                }}
              />
            </div>
            
            {/* Remote cursor overlay */}
            {remoteCursors.map((cursor) => (
              <div
                key={cursor.userId}
                className="absolute pointer-events-none z-50"
                style={{
                  left: `${cursor.x}px`,
                  top: `${cursor.y}px`,
                  transform: 'translate(-2px, -2px)',
                }}
              >
                <div
                  className="w-3 h-3 rounded-full border-2 border-white shadow-lg"
                  style={{ backgroundColor: cursor.user?.color || '#3B82F6' }}
                />
                <div
                  className="absolute top-4 left-2 bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap shadow-lg"
                  style={{ backgroundColor: cursor.user?.color || '#3B82F6' }}
                >
                  {cursor.user?.name || `User ${cursor.userId.slice(-4)}`}
                </div>
              </div>
            ))}

            {/* Connection status overlay */}
            {finalSocket?.isOffline && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40">
                <div className="bg-gray-900 text-white p-6 rounded-lg shadow-xl border border-gray-700">
                  <h3 className="text-lg font-semibold mb-2">Offline — Read Only</h3>
                  <p className="text-gray-300 mb-4">
                    Connection lost. Editor is in read-only mode.
                  </p>
                  <button
                    onClick={finalSocket?.reconnect}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                  >
                    Reconnect
                  </button>
                </div>
              </div>
            )}


            {/* Connected users list */}
            {finalSocket?.isConnected && finalSocket?.connectedUsers.length > 0 && (
              <div className="absolute top-4 left-4 bg-gray-900/90 text-white p-3 rounded-lg shadow-lg border border-gray-700 z-30" data-testid="editor-users-list" data-devinid="editor-users-list">
                <h4 className="text-sm font-semibold mb-2 text-gray-300">
                  Collaborators ({finalSocket?.connectedUsers.length})
                </h4>
                <div className="space-y-2">
                  {finalSocket?.connectedUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border border-white"
                        style={{ backgroundColor: user.color }}
                      />
                      <span className="text-sm text-gray-200">{user.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case 'diff':
        return (
          <div className="h-full p-4 space-y-4">
            <div className="text-center text-slate-400 mb-4">Diff Preview</div>
            <div className="bg-slate-800 rounded p-3 text-sm font-mono">
              <div className="text-red-400">- # Market analysis logic</div>
              <div className="text-green-400">+ # Enhanced market analysis with AI optimization</div>
              <div className="text-green-400">+ # Added error handling and performance improvements</div>
            </div>
          </div>
        );
      case 'browser':
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-col gap-2 p-3 border-b border-slate-700 bg-slate-900/50">
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={browserUrl}
                  onChange={(e) => setBrowserUrl(e.target.value)}
                  placeholder="Enter URL to browse and scan..."
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  data-devinid="browser-url-input"
                />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setPreviewUrl(browserUrl)} 
                  className="violett-button text-drop-shadow"
                >
                  Load
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={refreshPreview} disabled={isLoading} className="violett-button text-drop-shadow">
                    <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => window.open(previewUrl, '_blank')} className="violett-button text-drop-shadow">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Open
                  </Button>
                  {scanningMode === 'manual' && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => performOpticalScan(browserUrl)} 
                      disabled={isScanning}
                      className="violett-button text-drop-shadow"
                    >
                      <Camera className={`h-3 w-3 mr-1 ${isScanning ? 'animate-pulse' : ''}`} />
                      {isScanning ? 'Scanning...' : 'Scan'}
                    </Button>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Scanning:</span>
                  <div className="inline-flex bg-slate-800 rounded-md overflow-hidden">
                    {(['off', 'manual', 'automatic'] as const).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setScanningMode(mode)}
                        className={`px-2 py-1 text-xs font-medium capitalize ${
                          scanningMode === mode 
                            ? 'bg-violet-600 text-white' 
                            : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                        }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs">
                <div className="text-slate-400">
                  {isLoading ? 'Loading...' : 'Ready'}
                  {scanningMode === 'automatic' && (
                    <span className="ml-2 text-violet-400">• Auto-scanning every 30s</span>
                  )}
                </div>
                {lastScanTime && (
                  <div className="text-slate-500">
                    Last scan: {lastScanTime.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 flex">
              {!showFallback ? (
                <iframe
                  src={previewUrl}
                  className="flex-1 w-full border-0"
                  title="Universal Browser Scanner"
                  onError={handleIframeError}
                  onLoad={handleIframeLoad}
                />
              ) : (
                <div className="flex-1 bg-slate-900 flex items-center justify-center">
                  <div className="text-center text-slate-400 max-w-md">
                    <div className="text-lg mb-4">🚫 Site Blocks Embedding</div>
                    <div className="text-sm mb-4">
                      This site uses X-Frame-Options headers to prevent iframe embedding.
                      <br />
                      Use the scanning functionality to capture content instead.
                    </div>
                    <div className="text-xs text-slate-500">
                      URL: {previewUrl}
                    </div>
                    {scannedContent && (
                      <div className="mt-4 p-3 bg-slate-800 rounded text-left text-xs">
                        <div className="text-violet-400 mb-2">Latest Scan Result:</div>
                        <div className="text-slate-300">
                          <div className="font-medium">{scannedContent.title}</div>
                          <div className="mt-1 max-h-20 overflow-y-auto">
                            {scannedContent.text?.substring(0, 200)}...
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {scannedContent && (
                <div className="w-80 border-l border-slate-700 bg-slate-900/80 p-3 overflow-y-auto">
                  <h4 className="font-medium text-sm mb-2 text-violet-400">Scanned Content</h4>
                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-slate-400">Title:</span>
                      <div className="text-white mt-1">{scannedContent.title}</div>
                    </div>
                    {scannedContent.description && (
                      <div>
                        <span className="text-slate-400">Description:</span>
                        <div className="text-white mt-1">{scannedContent.description}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-slate-400">Content Preview:</span>
                      <div className="text-white mt-1 max-h-32 overflow-y-auto">
                        {scannedContent.text?.substring(0, 500)}...
                      </div>
                    </div>
                    {scannedContent.images?.length > 0 && (
                      <div>
                        <span className="text-slate-400">Images: {scannedContent.images.length}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'screenshots':
        return (
          <div className="h-full p-4 space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Playwright Screenshots</h4>
              <Button size="sm" variant="ghost" onClick={() => executeCommand('take-screenshot')} className="violett-button text-drop-shadow">
                <Camera className="h-3 w-3 mr-1" />
                Capture
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="text-center text-slate-400 py-8">
                No screenshots available. Use real backend API to load screenshots.
              </div>
            </div>
            <div className="mt-4 p-3 bg-slate-800/50 rounded text-xs">
              <div className="font-medium mb-1">Automation Status</div>
              <div className="text-slate-400">✓ Cross-browser testing enabled</div>
              <div className="text-slate-400">✓ Visual regression detection active</div>
              <div className="text-slate-400">✓ Performance metrics captured</div>
            </div>
          </div>
        );
      case 'canvas':
        return <ArcCanvasWorkspace />;
      case 'syntara':
        return <SyntaraCanvas />;
      default:
        return null;
    }
  };

  console.debug('[EnhancedMonacoEditor] Rendering main editor component');
  
  return (
    <ErrorBoundary fallback={
      <div className="flex items-center justify-center h-full bg-slate-900 text-white">
        <div className="text-center">
          <div className="text-lg font-medium mb-2">Editor Connection Issues</div>
          <div className="text-sm text-gray-400 mb-4">
            The editor is experiencing connectivity issues. Please refresh to continue.
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm"
          >
            Refresh Editor
          </button>
        </div>
      </div>
    }>
      <motion.div
        className="editor-container flex flex-col h-full w-full bg-gray-900/95 rounded-lg shadow-lg p-4"
        data-testid="enhanced-monaco-editor"
        data-devinid="workspace-editor-panel"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
      {/* Fixed header section - stays pinned */}
      <div className="flex-none px-4 py-2 bg-gray-900/90 rounded-t-lg border-b border-gray-800 shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
        <TopBar
          fileName="Code Editor"
          onRun={() => executeCommand('run')}
          onSave={() => executeCommand('save')}
          onDebug={() => executeCommand('debug')}
          onFixWithARCOS={handleFixWithARCOS}
          onTerminalToggle={handleTerminalToggle}
          layoutMode={layoutMode}
          onLayoutToggle={toggleLayoutMode}
          onUndo={handleUndo}
          onRedo={handleRedo}
        />

        {activeTab === 'editor' && (
          <OpenTabsBar
            files={[]}
            activeFile={activeFile}
            onFileSelect={(fileName: string) => {
              setActiveFile(fileName);
            }}
          />
        )}
        
        <div className="flex gap-1 p-2 border-b border-accent-warm-light/30">
          {tabs.map((tab) => (
            <Button
              key={tab}
              size="sm"
              variant={activeTab === tab ? "default" : "ghost"}
              onClick={() => setActiveTab(tab)}
              className="text-xs"
              data-tab={tab}
            >
              {tab === 'diff' && <Diff className="h-3 w-3 mr-1" />}
              {tab === 'browser' && <Globe className="h-3 w-3 mr-1" />}
              {tab === 'screenshots' && <Camera className="h-3 w-3 mr-1" />}
              {tab === 'canvas' && <BlueprintIcon className="h-3 w-3 mr-1" />}
              {tab === 'syntara' && <DesignIcon className="h-3 w-3 mr-1" />}
              {tab === 'editor' ? 'Editor' : 
               tab === 'diff' ? 'Diff' :
               tab === 'browser' ? 'Browser' :
               tab === 'screenshots' ? 'Screenshots' :
               tab === 'canvas' ? 'Canvas' :
               tab === 'syntara' ? 'Design' : tab}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Main editor content - grows and scrolls */}
      <div className="flex-1 overflow-auto min-h-0">
        {renderTabContent()}
      </div>

      {/* Terminal positioned properly */}
      <div className="flex-none border-t border-accent-warm-light/30" style={{ height: isTerminalMinimized ? '32px' : '120px' }}>
        <TerminalPane 
          height={isTerminalMinimized ? 32 : 120}
          onHeightChange={() => { /* intentionally empty */ }}
          isMinimized={isTerminalMinimized}
          onToggleMinimize={handleTerminalToggle}
        />
      </div>
      
      {/* Status bar at very bottom */}
      <div className="flex-none bg-slate-900 border-t border-accent-warm-light/30" style={{ height: '40px' }}>
        <EditorStatusBar
          fileName={activeFile}
          language={activeFile.endsWith('.py') ? 'python' : 'json'}
          testResults={testResults}
          performanceMetrics={performanceMetrics}
          buildCreditsUsage={logs.length * 5}
          aiProvider="arcOS"
        />
      </div>

      <Modal isOpen={showRefactorModal} onClose={() => setShowRefactorModal(false)} title="AI Refactor with arcOS">
        <div className="space-y-4">
          <p>arcOS AI will analyze and improve your code:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Optimize performance and memory usage</li>
            <li>Improve code readability and structure</li>
            <li>Add comprehensive error handling</li>
            <li>Follow Python best practices and PEP 8</li>
            <li>Add type hints and documentation</li>
          </ul>
          <div className="flex gap-2">
            <Button onClick={handleRefactor}>Apply Refactor</Button>
            <Button variant="ghost" onClick={() => setShowRefactorModal(false)} className="violett-button text-drop-shadow">Cancel</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} title="Command Palette">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => executeCommand('generate-tasks')} className="violett-button text-drop-shadow">
              <Zap className="h-3 w-3 mr-1" />
              Generate Tasks
            </Button>
            <Button size="sm" variant="ghost" onClick={() => executeCommand('cross-branch-search')} className="violett-button text-drop-shadow">
              <Search className="h-3 w-3 mr-1" />
              Cross-Branch Search
            </Button>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium">Branch Selection</h4>
            <div className="flex gap-1 flex-wrap">
              <div className="text-center text-slate-400 py-4">
                No branches available. Use real backend API to load branches.
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium">AI Suggestions</h4>
            <div className="space-y-1">
              <div className="text-center text-slate-400 py-4">
                No AI suggestions available. Use real backend API to load suggestions.
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showTaskInference} onClose={() => setShowTaskInference(false)} title="Task Inference">
        <div className="space-y-4">
          <p>AI-generated task suggestions based on your code:</p>
          <div className="space-y-2">
            <div className="p-3 bg-slate-800 rounded">
              <div className="font-medium text-sm">Optimization Opportunity</div>
              <div className="text-xs text-slate-400 mt-1">
                Consider implementing connection pooling for database operations
              </div>
            </div>
            <div className="p-3 bg-slate-800 rounded">
              <div className="font-medium text-sm">Code Quality</div>
              <div className="text-xs text-slate-400 mt-1">
                Add input validation for the analyze_market function
              </div>
            </div>
            <div className="p-3 bg-slate-800 rounded">
              <div className="font-medium text-sm">Testing</div>
              <div className="text-xs text-slate-400 mt-1">
                Create unit tests for market analysis logic
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showCrossBranchSearch} onClose={() => setShowCrossBranchSearch(false)} title="Cross-Branch Search Results">
        <div className="space-y-4">
          <p>Search results across all branches:</p>
          <div className="space-y-2">
            <div className="p-3 bg-slate-800 rounded">
              <div className="font-medium text-sm">feature/optimization</div>
              <div className="text-xs text-slate-400 mt-1">
                Found 3 matches in trading_algorithm.py
              </div>
            </div>
            <div className="p-3 bg-slate-800 rounded">
              <div className="font-medium text-sm">develop</div>
              <div className="text-xs text-slate-400 mt-1">
                Found 2 matches in market_analyzer.py
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </motion.div>
    </ErrorBoundary>
  );
};

export default EnhancedMonacoEditor;
